import {
  DEFAULT_MAP_LAYERS,
  MAP_DISPLAY_MODES,
  NA_COLOR,
  NULL_GROUP_LABEL,
  PLOT_LABEL_ID,
} from "../constants";
import { GeoJSON, useMap } from "react-leaflet";
import { boolStringToInt, escapeHtml, truncateNumber } from "../services/utils";
import { memo, useEffect, useMemo, useState } from "react";
import {
  selectColorScale,
  selectFilteredFeatures,
  selectMinZoomToShowLabels,
  selectShowPlotLabels,
} from "../selectors/resultMap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import L from "leaflet";
import { centroid } from "@turf/turf";
import chroma from "chroma-js";
import { flyToPlot } from "./utils";
import { setSelectedPlotId } from "../actions/resultMap";
import { useAttributeColor } from "../hooks/useAttributeColor";

const FEATURE_DEFAULT_STYLE = {
  fillOpacity: 0.7,
  weight: 0.2,
  opacity: 1,
  color: "white",
};

const FEATURE_OUTLINE_STYLE = { ...FEATURE_DEFAULT_STYLE, fillOpacity: 0 };

const FEATURE_WITH_NO_TRAIT_STYLE = {
  weight: 3,
  opacity: 1,
  color: "skyblue",
  fillOpacity: 0,
};

// Plot Map without colouring: the slider fades the blue outlines into the
// thin white one of FEATURE_OUTLINE_STYLE, reached at 0, with no jump.
const getPlotMapOutlineStyle = (opacity) => ({
  ...FEATURE_WITH_NO_TRAIT_STYLE,
  color: chroma
    .mix(
      FEATURE_OUTLINE_STYLE.color,
      FEATURE_WITH_NO_TRAIT_STYLE.color,
      opacity
    )
    .hex(),
  weight:
    FEATURE_OUTLINE_STYLE.weight +
    (FEATURE_WITH_NO_TRAIT_STYLE.weight - FEATURE_OUTLINE_STYLE.weight) *
      opacity,
});

const FEATURE_WITH_BUFFER_STYLE = {
  weight: 3,
  opacity: 1,
  color: "orange",
  fillOpacity: 1,
};

const FEATURE_SELECTED_DEFAULT_STYLE = {
  weight: 3.5,
  color: "white",
  dashArray: "",
  fillOpacity: 0.7,
};

const FEATURE_SIMILAR_DEFAULT_STYLE = {
  weight: 2,
  color: "lightgray",
  dashArray: "",
  fillOpacity: 0.7,
};

// memo: the parent (TrialDisplay) reads the whole resultMap and re-renders on
// every dispatch, and each render here remounts every plot polygon (the
// GeoJSON key below). Only this component's own selectors should redraw it.
export const FeaturesLayer = memo(function FeaturesLayer() {
  const dispatch = useDispatch();
  const features = useSelector(selectFilteredFeatures);
  const minZoomToShowLabels = useSelector(selectMinZoomToShowLabels);
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [bounds, setBounds] = useState(map.getBounds());

  const [
    mapLayers,
    selectedPlotId,
    selectedTrait,
    plotLabelProperty,
    plotLabelOpacity,
    mapDisplayMode,
    selectedAttribute,
    plotMapColoring,
    plotMapOpacity,
    linearRulerActive,
  ] = useSelector(
    ({ resultMap }) => [
      resultMap.mapLayers,
      resultMap.selectedPlotId,
      resultMap.selectedTrait,
      resultMap.plotLabelProperty,
      resultMap.plotLabelOpacity,
      resultMap.mapDisplayMode,
      resultMap.selectedAttribute,
      resultMap.plotMapColoring,
      resultMap.plotMapOpacity,
      resultMap.linearRulerActive,
    ],
    shallowEqual
  );

  const isPlotMapMode = mapDisplayMode === MAP_DISPLAY_MODES.PLOT_MAP;
  // Plot Map shows attribute colours too once picked in its palette editor.
  const isFieldBookMode =
    mapDisplayMode === MAP_DISPLAY_MODES.FIELD_BOOK ||
    (isPlotMapMode && plotMapColoring);
  const getAttributeColor = useAttributeColor(selectedAttribute);
  const layer = useMemo(
    () => mapLayers.find(({ id }) => id === DEFAULT_MAP_LAYERS.FEATURES_LAYER),
    [mapLayers]
  );
  const colorScale = useSelector(selectColorScale);
  const showLabels = useSelector(selectShowPlotLabels);
  // One chroma mix per slider value, not one per plot on every render.
  const plotMapOutlineStyle = useMemo(
    () => getPlotMapOutlineStyle(plotMapOpacity),
    [plotMapOpacity]
  );

  const selectedFeature = features.find((f) => f.id === selectedPlotId);

  const getFeatureStyle = (feature) => {
    if (feature.displayId === "buffer") return FEATURE_WITH_BUFFER_STYLE;

    if (isPlotMapMode && !plotMapColoring) return plotMapOutlineStyle;

    if (isFieldBookMode)
      return {
        ...FEATURE_DEFAULT_STYLE,
        fillOpacity: isPlotMapMode
          ? plotMapOpacity * FEATURE_DEFAULT_STYLE.fillOpacity
          : layer.opacity,
        fillColor: getAttributeColor(feature.properties[selectedAttribute]),
      };

    if (!feature.trait_list.length) return FEATURE_WITH_NO_TRAIT_STYLE;

    // N/A is drawn here: the colour scale only exists when the trait has a
    // range, so a trait with no value at all would leave the plot unfilled.
    const value = feature.properties[selectedTrait.technical_name];
    return {
      ...FEATURE_DEFAULT_STYLE,
      fillOpacity: layer.opacity,
      fillColor:
        value == null ? NA_COLOR : colorScale.func(boolStringToInt(value)),
    };
  };

  // function bind to each microplot on hover and on click
  const onEachFeature = (feature, layer) => {
    // Leaflet injects the tooltip as html: the plot id, trait name and value
    // come from client uploads, escaped like the labels.
    layer.bindTooltip(
      "<div><b>Plot id: </b> " +
        escapeHtml(feature.displayId) +
        "</div>" +
        (selectedTrait.name
          ? "<div><b>" +
            escapeHtml(selectedTrait.name) +
            "</b>: " +
            escapeHtml(
              truncateNumber(
                feature.properties[selectedTrait.technical_name] ??
                  NULL_GROUP_LABEL
              )
            ) +
            "</div>"
          : "")
    );
    if (selectedPlotId)
      if (layer.feature.id === selectedPlotId) {
        layer.bringToFront();
        layer.setStyle(FEATURE_SELECTED_DEFAULT_STYLE);
      } else if (layer.feature.group === selectedFeature?.group) {
        layer.setStyle(FEATURE_SIMILAR_DEFAULT_STYLE);
      }
    layer.on({
      click: () => {
        dispatch(setSelectedPlotId(feature.id));
        // Measuring: the click places a ruler point, the map stays put.
        if (!linearRulerActive)
          flyToPlot(map, layer.getBounds(), minZoomToShowLabels);
      },
    });
  };
  useEffect(() => {
    const updateMapState = () => {
      setZoom(map.getZoom());
      setBounds(map.getBounds());
    };
    map.on("moveend", updateMapState);
    map.on("zoomend", updateMapState);

    return () => {
      map.off("zoomend", updateMapState);
      map.off("moveend", updateMapState);
    };
  }, [map]);

  /* the centroids are computed once per set of features, then the
   * labelLayer useMemo is used after just to filter after we zoom and move on
   * the map ( the centers are already calculated )
   */
  const featuresWithCenter = useMemo(
    () => features.map((feat) => ({ feat, center: centroid(feat) })),
    [features]
  );

  const [featuresVersion, setFeaturesVersion] = useState({ features, key: 0 });
  if (featuresVersion.features !== features)
    setFeaturesVersion({ features, key: featuresVersion.key + 1 });

  // Label each centroid with the property picked in the legend: cheap, so a
  // change of option does not recompute the centroids.
  // Plots without a value for it get no label rather than an empty one.
  const labelPoints = useMemo(() => {
    if (!plotLabelProperty) return [];
    return featuresWithCenter.flatMap(({ feat, center }) => {
      const label =
        plotLabelProperty === PLOT_LABEL_ID
          ? feat.displayId
          : feat.properties[plotLabelProperty];
      if (label == null || `${label}` === "") return [];
      return [{ ...center, label }];
    });
  }, [featuresWithCenter, plotLabelProperty]);

  const labelLayer = useMemo(() => {
    // Early return if zoom is too low or map is not available
    if (zoom < minZoomToShowLabels || !map || !bounds) return null;

    // Filter features based on current bounds
    const filteredFeatures = labelPoints.filter((feature) => {
      const [lng, lat] = feature.geometry.coordinates;

      // Check if feature is within current map bounds
      return (
        lat >= bounds.getSouthWest().lat &&
        lat <= bounds.getNorthEast().lat &&
        lng >= bounds.getSouthWest().lng &&
        lng <= bounds.getNorthEast().lng
      );
    });

    // Create GeoJSON layer with filtered features
    return (
      <GeoJSON
        // react-leaflet's GeoJSON never updates its data: the features version
        // (filter, date, edited attribute), the label property and opacity are
        // in the key so a new value remounts it.
        key={`geojson-${
          featuresVersion.key
        }-${plotLabelProperty}-${plotLabelOpacity}-${
          bounds.getSouthWest().lat
        }-${bounds.getNorthEast().lng}`}
        data={{
          type: "FeatureCollection",
          features: filteredFeatures,
        }}
        pointToLayer={(geoJsonPoint, latlng) =>
          L.marker(latlng, {
            icon: L.divIcon({
              className: "",
              html: `<b style="transform:translate(-50%, -50%); color: white;text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;">
              ${escapeHtml(geoJsonPoint.label)}
            </b>`,
              iconSize: "auto",
            }),
            interactive: false,
            opacity: plotLabelOpacity,
          })
        }
      />
    );
  }, [
    minZoomToShowLabels,
    labelPoints,
    featuresVersion.key,
    plotLabelProperty,
    plotLabelOpacity,
    bounds,
    zoom,
    map,
  ]);

  return (
    <>
      {layer.visible ? (
        <GeoJSON
          key={new Date().getTime()}
          data={features}
          style={getFeatureStyle}
          onEachFeature={onEachFeature}
        />
      ) : (
        showLabels && (
          <GeoJSON
            key={featuresVersion.key}
            data={features}
            style={FEATURE_OUTLINE_STYLE}
            interactive={false}
          />
        )
      )}
      {showLabels && labelLayer}
    </>
  );
});
